import React, { useState } from "react";
import { ActionIcon, Tooltip, Text, Box } from "@mantine/core";
import {
  IconPlus,
  IconCalendarPlus,
  IconWalk,
  IconClockOff,
  IconShoppingCart,
} from "@tabler/icons-react";
import styles from "./FloatingActionButton.module.css";

interface FABAction {
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
  color?: string;
}

interface FloatingActionButtonProps {
  onNewAppointment: () => void;
  /** Opens the walk-ins drawer on the New walk-in form. */
  onNewWalkIn: () => void;
  onNewTimeBlock: () => void;
  onNewQuickSale: () => void;
  /** A calendar drawer is open: shift left of it instead of hiding. */
  shifted?: boolean;
}

const FloatingActionButton: React.FC<FloatingActionButtonProps> = ({
  onNewAppointment,
  onNewWalkIn,
  onNewTimeBlock,
  onNewQuickSale,
  shifted = false,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  // Order: Bottom to top - Time Block, Quick Sale, Walk-In, Appointment
  const actions: FABAction[] = [
    {
      icon: <IconClockOff size={20} />,
      label: "Add Time Block",
      onClick: () => {
        onNewTimeBlock();
        setIsExpanded(false);
      },
      color: "var(--color-accent)",
    },
    {
      icon: <IconShoppingCart size={20} />,
      label: "Quick Sale",
      onClick: () => {
        onNewQuickSale();
        setIsExpanded(false);
      },
      color: "var(--color-primary)",
    },
    {
      icon: <IconWalk size={20} />,
      label: "New Walk-In",
      onClick: () => {
        onNewWalkIn();
        setIsExpanded(false);
      },
      color: "var(--color-secondary)",
    },
    {
      icon: <IconCalendarPlus size={20} />,
      label: "New Appointment",
      onClick: () => {
        onNewAppointment();
        setIsExpanded(false);
      },
      color: "var(--color-primary)",
    },
  ];

  const handleToggle = () => {
    setIsExpanded(!isExpanded);
  };

  // Calculate positions for vertical stack
  const getButtonPosition = (index: number) => {
    // Stack vertically above the main button
    // Each button is 48px + 12px gap = 60px spacing
    const spacing = 60;
    const yOffset = -(spacing * (index + 1));

    return {
      x: 0,
      y: yOffset,
    };
  };

  return (
    <>
      {/* Backdrop when expanded */}
      {isExpanded && (
        <Box className={styles.backdrop} onClick={() => setIsExpanded(false)} />
      )}

      <div
        className={`${styles.container} ${shifted ? styles.containerShifted : ""}`}
        data-shifted={shifted}
      >
        {/* Expanded action buttons */}
        <div className={styles.actionsContainer}>
          {actions.map((action, index) => {
            const position = getButtonPosition(index);
            return (
              <div
                key={action.label}
                className={`${styles.actionWrapper} ${isExpanded ? styles.actionWrapperVisible : ""}`}
                style={
                  {
                    "--target-x": `${position.x}px`,
                    "--target-y": `${position.y}px`,
                    "--delay": `${index * 50}ms`,
                  } as React.CSSProperties
                }
              >
                <ActionIcon
                  className={styles.actionButton}
                  variant="filled"
                  color={action.color}
                  size={48}
                  radius="xl"
                  onClick={action.onClick}
                >
                  {action.icon}
                </ActionIcon>
                <Text
                  className={styles.actionLabel}
                  size="sm"
                  fw={500}
                  c="white"
                >
                  {action.label}
                </Text>
              </div>
            );
          })}
        </div>

        {/* Main FAB button - Plus icon rotates 45° to become X */}
        <Tooltip
          label="Add new"
          position="left"
          withArrow
          disabled={isExpanded}
        >
          <ActionIcon
            className={`${styles.mainButton} ${isExpanded ? styles.mainButtonExpanded : ""}`}
            variant="filled"
            color={isExpanded ? "gray" : "var(--color-primary)"}
            size={56}
            radius="xl"
            onClick={handleToggle}
          >
            <IconPlus size={24} className={styles.plusIcon} />
          </ActionIcon>
        </Tooltip>
      </div>
    </>
  );
};

export default FloatingActionButton;
